/* eslint-disable indent */
import axios, { AxiosResponse } from 'axios';
import store from '@/store';
import router from '@/router';
import RootTypes from '@/store/types/root';
import authType from '@/store/types/auth';
import { AuthState } from '@/store/interfaces/auth';
import { hasPermission } from '@/util/auth';
import { permissions } from '@/router/menus/permissions';

const tokenKey = '_nemipay_api_token';
const URL: string | undefined = `${process.env.VUE_APP_URL_PAYMENTS_API}/api`;

let isLoggingOut = false;

const httpCommpix = axios.create({
  baseURL: `${URL}`,
});
// Add a request interceptor
httpCommpix.interceptors.request.use(
  config => {
    store.commit(RootTypes.mutations.SET_START_PROCESS);
    const token = localStorage.getItem(tokenKey);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers?.Authorization;
    }
    return config;
  },
  error => {
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    return Promise.reject(error);
  }
);

// Add a response interceptor
httpCommpix.interceptors.response.use(
  (response: AxiosResponse) => {
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    return response;
  },
  error => {
    store.commit(RootTypes.mutations.SET_END_PROCESS);
    if (error.response && error.response.status === 401 && !isLoggingOut) {
      const requestUrl = error.config?.url ?? '';
      const isLoginOrInvalidate = typeof requestUrl === 'string' &&
        (requestUrl.includes('/login/invalidate') || requestUrl === 'login' || requestUrl.endsWith('/login'));
      if (!isLoginOrInvalidate) {
        const isAuthenticated = store.getters['authModule/' + authType.getters.IS_AUTHENTICATED];
        if (isAuthenticated) {
          isLoggingOut = true;
          store.dispatch('authModule/' + authType.actions.AUTH_LOGOUT).then(() => {
            router.push({ name: 'Login', });
          }).finally(() => {
            isLoggingOut = false;
          });
        }
      }
    }

    // Extract error message
    const errorDetails = error.response?.data?.details;
    const errorMessage = Array.isArray(errorDetails) && errorDetails.length > 0
      ? errorDetails[0]
      : error;

    return Promise.reject(errorMessage);
  }
);

export function getAuthPayload(): {
  companyId: string | null | undefined;
  commerceId: string | null | undefined;
  branchId: string | null | undefined;
  username: string | null | undefined;
} {
  const auth = ((store.state as unknown) as { authModule: AuthState }).authModule;
  const activeCompanyUser = auth?.currentUser?.company_users?.[0];
  return {
    companyId: activeCompanyUser?.company_id,
    commerceId: activeCompanyUser?.commerce_id,
    branchId: activeCompanyUser?.branch_id,
    username: auth?.currentUser?.email,
  };
}

export async function postWithAuthPayload<T = unknown>(
  url: string,
  data: Record<string, unknown> = {}
): Promise<AxiosResponse<T>> {
  const payload = { ...getAuthPayload(), ...data, };
  return httpCommpix.post<T>(url, payload);
}

export async function postWithAuthPayloadFiltered<T = unknown>(
  url: string,
  data: Record<string, unknown> = {}
): Promise<AxiosResponse<T>> {
  const auth = ((store.state as unknown) as { authModule: AuthState }).authModule;
  const userPermissions: string[] = auth?.permission_groups ?? [];
  const shouldIncludeUsername = hasPermission(
    userPermissions,
    permissions.HISTORY_TRANSACTION.VIEW_MY_TRANSACTIONS
  );

  const { username, ...basePayload } = getAuthPayload();
  const payload = {
    ...basePayload,
    ...(shouldIncludeUsername && { username, }),
    ...data,
  };
  return httpCommpix.post<T>(url, payload);
}

export { httpCommpix, URL, tokenKey };
